import { Text, tokens, Button } from '@hotjar/hotsauce';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import type { Site } from '@hotjar/site';
import { SkuDropdown } from '../../external-dependencies/features/Billing/MultiSkuDropdown';
import type {
  FeedbackProduct,
  AnalyticsProduct,
} from '../../external-dependencies/domains/product/types';
import { ProductType } from '../../external-dependencies/domains/product/types';
import { useTrack } from '../../external-dependencies/domains/tracking/useTrack';
import { MODAL } from '../../constants';
import { PlanSelectionStyles as S } from './PlanSelection.styles';

interface PlanSelectionProps {
  onPlanChange: (params: AnalyticsProduct | FeedbackProduct) => void;
  productType: ProductType;
  selectedProductSKU: string | undefined;
  products: Array<FeedbackProduct | AnalyticsProduct>;
  site: Site;
}

export const PlanSelection = ({
  selectedProductSKU,
  productType,
  onPlanChange,
  products,
  site,
}: PlanSelectionProps): JSX.Element => {
  const [editing, setEditing] = useState(false);
  const { click: trackClick, getGroupDataForSite } = useTrack();

  const { t } = useTranslation('trials', { keyPrefix: 'post-trial.plan-selection' });

  const activeProduct =
    products.find((product) => product.sku === selectedProductSKU) ?? products[0];

  const onEdit = () => {
    const uiPosition =
      'dailyCcSessions' in activeProduct ? MODAL.editObserveProduct : MODAL.editAskProduct;

    trackClick(MODAL.name, uiPosition, getGroupDataForSite(site));
    setEditing(true);
  };

  if (editing) {
    return (
      <S.PlanSelection>
        <SkuDropdown
          style={{ width: '100%' }}
          onChange={onPlanChange}
          products={products}
          activeProduct={activeProduct}
          productType={
            productType === ProductType.Analytics ? 'analyticsProducts' : 'feedbackProducts'
          }
          showTitle={false}
          reversePlanBadges
          siteAccount={site.account}
        />
      </S.PlanSelection>
    );
  }

  return (
    <S.PlanSelection>
      {t('plan', { tier: capitalize(activeProduct.tier) })}
      <Text textColor="text_light" style={{ marginLeft: tokens.spacer.x1pt5 }}>
        {'dailyCcSessions' in activeProduct
          ? t('observe_sessions', { nrOfSessions: localeNumber(activeProduct.dailyCcSessions) })
          : t('ask_responses', { nrOfResponses: localeNumber(activeProduct.monthlyResponses) })}
      </Text>
      <Button
        kind="ghost_standout"
        size="s"
        onClick={onEdit}
        data-testid={`edit_icon_${productType}`}
        style={{
          marginLeft: tokens.spacer.x1,
        }}
      >
        {t('edit')}
      </Button>
    </S.PlanSelection>
  );
};
